import { forwardRef, useEffect, useImperativeHandle, useState, useCallback } from 'react'
import {
  Search,
  Type,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Minus,
  ImagePlus,
  CheckSquare,
} from 'lucide-react'
import type { SlashCommandItem } from '../extensions/slashCommand'

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Type,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Minus,
  ImagePlus,
  CheckSquare,
}

export interface SlashMenuRef {
  onKeyDown: (event: KeyboardEvent) => boolean
}

interface SlashMenuProps {
  items: SlashCommandItem[]
  command: (item: SlashCommandItem) => void
  query?: string
}

export const SlashMenu = forwardRef<SlashMenuRef, SlashMenuProps>(
  ({ items, command, query = '' }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    // Reset selection when items change
    useEffect(() => {
      setSelectedIndex(0) // eslint-disable-line react-hooks/set-state-in-effect -- intentional reset on data change
    }, [items])

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index]
        if (item) {
          command(item)
        }
      },
      [items, command]
    )

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          setSelectedIndex((prev) => (prev - 1 + items.length) % items.length)
          return true
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault()
          setSelectedIndex((prev) => (prev + 1) % items.length)
          return true
        }

        if (event.key === 'Enter') {
          event.preventDefault()
          selectItem(selectedIndex)
          return true
        }

        return false
      },
    }))

    return (
      <div
        className="slash-menu rounded-xl border overflow-hidden shadow-xl"
        style={{
          backgroundColor: 'hsl(var(--color-bg-secondary))',
          borderColor: 'hsl(var(--color-border))',
        }}
      >
        {/* Search field */}
        <div
          className="flex items-center gap-1.5 px-2 py-1 border-b"
          style={{ borderColor: 'hsl(var(--color-border))' }}
        >
          <Search
            className="w-3 h-3 flex-shrink-0"
            style={{ color: 'hsl(var(--color-text-muted))' }}
          />
          {query ? (
            <span
              className="text-[11px]"
              style={{ color: 'hsl(var(--color-text-primary))' }}
            >
              {query}
            </span>
          ) : (
            <span
              className="text-[11px]"
              style={{ color: 'hsl(var(--color-text-muted))' }}
            >
              Type to filter…
            </span>
          )}
        </div>

        {/* Items */}
        <div className="slash-menu-items">
        {items.length === 0 ? (
          <div className="px-2.5 py-2">
            <p
              className="text-xs"
              style={{ color: 'hsl(var(--color-text-muted))' }}
            >
              No results
            </p>
          </div>
        ) : (
            <div className="py-0.5">
              {items.map((item, index) => {
                const Icon = iconMap[item.icon]
                const isActive = index === selectedIndex

                return (
                  <button
                    key={item.id}
                    ref={isActive ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
                    className="w-full flex items-center gap-1.5 px-2 py-[2px] text-left transition-fast"
                    style={{
                      backgroundColor: isActive
                        ? 'hsl(var(--color-bg-tertiary))'
                        : 'transparent',
                      color: 'hsl(var(--color-text-primary))',
                    }}
                    onClick={() => selectItem(index)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span
                      className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border"
                      style={{
                        backgroundColor: 'hsl(var(--color-bg-primary))',
                        borderColor: 'hsl(var(--color-border))',
                        color: 'hsl(var(--color-text-secondary))',
                      }}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                    </span>
                    <span className="flex-1 text-[11px]">{item.label}</span>
                    {item.shortcut && (
                      <span
                        className="text-[10px] font-mono"
                        style={{ color: 'hsl(var(--color-text-muted))' }}
                      >
                        {item.shortcut}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
        )}
        </div>
      </div>
    )
  }
)

SlashMenu.displayName = 'SlashMenu'
